import { useMountEffect, useUnmountEffect } from "@react-hookz/web";
import { useEffect, useRef, useState } from "react";
import { LooopPlayer } from "shared/AudioPlayers/LooopPlayer/LooopPlayer";
import { EQPlaybackControls } from "shared/AudioPlayers/types";
import {
  LooopPlayerSyncParams,
  PartialLooopPlayerSyncParams,
} from "shared/AudioPlayers/LooopPlayer/types";
import { MixPuzzle, MixPuzzleInstrument } from "shared/games/mixpuzzles/types";
import { State } from "../state/reducer";
import {
  getInstrumentsBeingHeard,
  getInstrumentsAvailable,
} from "../state/selectors";
import getPan from "shared/games/mixpuzzles/getPan";

interface Params {
  state: State;
  onLoaded: () => void;
  initialPlayState: boolean;
}

export function useAudioChangeController({
  state,
  onLoaded,
  initialPlayState,
}: Params) {
  const [playing, setPlaying] = useState(initialPlayState);
  const instrumentsBeingHeard = getInstrumentsBeingHeard(state);
  const looopPlayer = useRef<LooopPlayer>();

  useMountEffect(() => {
    const defaultParams = convertStateToLooopPlayerParams({
      mixPuzzle: state.mixPuzzle,
      instrumentsBeingHeard,
      grade: state.grade,
      activeInstrument: state.instrument,
      muted: state.muted,
      soloed: state.soloed,
    });
    const player = new LooopPlayer(
      state.mixPuzzle.looop,
      undefined,
      defaultParams
    );
    looopPlayer.current = player;

    player.load().then(() => {
      onLoaded();

      if (!playing) {
        return;
      }

      looopPlayer.current?.start();
    });
  });

  useUnmountEffect(() => {
    looopPlayer.current?.dispose();
    looopPlayer.current = undefined;
  });

  useEffect(() => {
    if (!looopPlayer.current) {
      return;
    }

    if (playing) {
      looopPlayer.current?.start();
    } else {
      looopPlayer.current?.stop();
    }
  }, [playing]);

  useEffect(() => {
    if (!looopPlayer.current) {
      return;
    }

    const params = convertStateToLooopPlayerParams({
      mixPuzzle: state.mixPuzzle,
      instrumentsBeingHeard,
      grade: state.grade,
      activeInstrument: state.instrument,
      muted: state.muted,
      soloed: state.soloed,
    });

    looopPlayer.current?.sync(params);
  }, [
    instrumentsBeingHeard,
    state.grade,
    state.instrument,
    state.mixPuzzle,
    state.muted,
    state.soloed,
  ]);

  return { playing, setPlaying };
}

function convertStateToLooopPlayerParams({
  instrumentsBeingHeard,
  mixPuzzle,
  grade,
  activeInstrument,
  muted: mutedInstruments,
  soloed,
}: {
  instrumentsBeingHeard: { [instrument: string]: MixPuzzleInstrument };
  mixPuzzle: MixPuzzle;
  grade: State["grade"];
  soloed: State["soloed"];
  muted: State["muted"];
  activeInstrument: State["instrument"];
}) {
  const params: PartialLooopPlayerSyncParams = {};

  Object.values(mixPuzzle.looop.instruments).forEach(({ instrument }) => {
    const currentInstrument = instrumentsBeingHeard[instrument];

    // This is the case when a mixpuzzle doesn't use all of a looops tracks -- the ones not used should just be muted
    if (!currentInstrument) {
      params[instrument] = {
        instrument,
        audioFiles: {
          default: {
            audioFile: "default",
            controls: {
              muted: true,
              eq: {},
              pan: getPan("C"),
              volume: 0,
            },
          },
          comp_opto4: {
            audioFile: "comp_opto4",
            controls: {
              muted: true,
              eq: {},
              pan: getPan("C"),
              volume: 0,
            },
          },
        },
      };

      return;
    }

    const eq = instrumentToEQPlaybackControls(currentInstrument);
    const muted = (() => {
      if (grade) {
        return instrument !== activeInstrument;
      }

      if (soloed !== null) {
        return soloed !== instrument;
      }

      return mutedInstruments.has(instrument);
    })();

    params[instrument] = {
      instrument,
      audioFiles: {
        default: {
          audioFile: "default",
          controls: {
            eq,
            muted: muted ? true : currentInstrument.comp !== 7 ? false : true,
            pan: getPan(currentInstrument.pan),
            volume: currentInstrument.volume,
          },
        },
        comp_opto7: {
          audioFile: "comp_opto7",
          controls: {
            eq,
            muted: muted ? true : currentInstrument.comp === 7 ? false : true,
            pan: getPan(currentInstrument.pan),
            volume: currentInstrument.volume,
          },
        },
      },
    };
  });

  return params;
}

function instrumentToEQPlaybackControls(
  instrument: MixPuzzleInstrument
): EQPlaybackControls {
  const bass = 80;
  const lmid = 325;
  const hmid = 1250;
  const high = 6000;

  return {
    [bass]: {
      frequency: bass,
      gain: instrument.eq_freq === "bass" ? +instrument.eq_gain : 0,
      q: 1,
      type: "lowshelf",
    },

    [lmid]: {
      frequency: lmid,
      gain: instrument.eq_freq === "lmid" ? +instrument.eq_gain : 0,
      q: 1,
      type: "lowshelf",
    },

    [hmid]: {
      frequency: hmid,
      gain: instrument.eq_freq === "hmid" ? +instrument.eq_gain : 0,
      q: 1,
      type: "highshelf",
    },

    [high]: {
      frequency: high,
      gain: instrument.eq_freq === "high" ? +instrument.eq_gain : 0,
      q: 1,
      type: "highshelf",
    },
  };
}
